import React from 'react';
import { Brain, ArrowUpRight, Clock, Building2 } from 'lucide-react';
import Link from 'next/link';

const insights = [
  {
    id: 'insight-001',
    title: 'Taiwan Strait — Escalation Window',
    body: 'PLA naval exercise pattern matches pre-2022 Pelosi visit posture. AI model assigns 55% probability of further escalation within 10 days. 3 Acme Corp Taiwan-based suppliers at elevated procurement risk.',
    severity: 'high',
    confidence: 81,
    time: '12 min ago',
    tag: 'Regional Risk',
    href: '/political-risk',
    acmeFlag: true,
  },
  {
    id: 'insight-002',
    title: 'OFAC Update — Supplier Screening Required',
    body: 'New Iranian designations affect 2 Acme Corp Tier-1 suppliers in petrochemical supply chain. Immediate screening and transaction suspension required to maintain compliance.',
    severity: 'critical',
    confidence: 97,
    time: '1 hr ago',
    tag: 'Sanctions',
    href: '/sanctions-tracker',
    acmeFlag: true,
  },
  {
    id: 'insight-003',
    title: 'Red Sea Routing — Cost Trajectory',
    body: 'Cape of Good Hope rerouting adds average 13.2 days transit. Freight insurance premiums up 24% WoW. 1 Acme Corp shipment confirmed rerouted — downstream delivery windows impacted.',
    severity: 'high',
    confidence: 93,
    time: '2 hr ago',
    tag: 'Maritime',
    href: '/maritime-risk',
    acmeFlag: true,
  },
  {
    id: 'insight-004',
    title: 'APT41 — APAC Financial Exposure',
    body: 'New IOCs published by CISA. Campaign targets SWIFT messaging infrastructure. 3 monitored IP ranges match Acme Corp APAC network profile. Network segmentation review recommended.',
    severity: 'medium',
    confidence: 76,
    time: '3 hr ago',
    tag: 'Cyber',
    href: '/cyber-risk',
    acmeFlag: false,
  },
];

const severityAccent: Record<string, string> = {
  critical: 'border-l-danger',
  high: 'border-l-warning',
  medium: 'border-l-info',
  low: 'border-l-primary',
};

const tagColors: Record<string, string> = {
  Sanctions: 'text-danger bg-danger/10',
  'Regional Risk': 'text-orange-400 bg-orange-500/10',
  Maritime: 'text-primary bg-primary/10',
  Cyber: 'text-info bg-info/10',
};

export default function AIInsightsSidebar() {
  return (
    <div className="card-elevated rounded-xl border border-border h-full flex flex-col">
      <div className="flex items-center gap-2 p-4 border-b border-border">
        <div className="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
          <Brain size={14} className="text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">AI Insights</p>
          <p className="text-2xs text-muted-foreground">Company-specific · live</p>
        </div>
        <span className="ml-auto relative flex h-2 w-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75" />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-primary" />
        </span>
      </div>

      <div className="flex-1 overflow-y-auto divide-y divide-border">
        {insights.map((insight) => (
          <Link
            key={insight.id}
            href={insight.href}
            className={`p-4 border-l-2 ${severityAccent[insight.severity]} hover:bg-muted/30 transition-colors cursor-pointer group block`}
          >
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <p className="text-xs font-semibold text-foreground leading-snug flex-1">{insight.title}</p>
              <ArrowUpRight size={12} className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
            </div>
            {insight.acmeFlag && (
              <div className="flex items-center gap-1 mb-1.5">
                <Building2 size={10} className="text-warning flex-shrink-0" />
                <span className="text-2xs text-warning font-medium">Acme Corp exposure</span>
              </div>
            )}
            <p className="text-2xs text-muted-foreground leading-relaxed mb-3">{insight.body}</p>
            <div className="flex items-center justify-between">
              <span className={`text-2xs px-1.5 py-0.5 rounded font-medium ${tagColors[insight.tag] || 'text-muted-foreground bg-muted'}`}>
                {insight.tag}
              </span>
              <div className="flex items-center gap-2 text-2xs text-muted-foreground">
                <span className="font-tabular">{insight.confidence}% conf.</span>
                <span>·</span>
                <Clock size={9} />
                <span>{insight.time}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="p-3 border-t border-border">
        <Link href="/ai-reports" className="w-full text-xs text-center text-primary hover:text-primary/80 font-medium transition-colors py-1 block">
          View all intelligence briefs →
        </Link>
      </div>
    </div>
  );
}